import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Link } from "react-router-dom";
import { Loader2, User, Fingerprint } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import { useGoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import axios from "axios";

const BG_IMAGES = [
  { url: "https://images.unsplash.com/photo-1682687219573-3fd75f982217?w=1920&q=80", caption: "AlUla", captionAr: "العلا" },
  { url: "https://images.unsplash.com/photo-1609151712779-4f86b3de7308?w=1920&q=80", caption: "AlUla Heritage", captionAr: "تراث العلا" },
  { url: "https://images.unsplash.com/photo-1730917889738-cbaea7f9e4dc?w=1920&q=80", caption: "Hegra, AlUla", captionAr: "الحجر، العلا" },
  { url: "https://images.unsplash.com/photo-1506795213373-430e921fe2ed?w=1920&q=80", caption: "Kingdom Centre, Riyadh", captionAr: "برج المملكة، الرياض" },
  { url: "https://images.unsplash.com/photo-1646662521253-5b9253b1a207?w=1920&q=80", caption: "King Abdullah Financial District", captionAr: "مركز الملك عبدالله المالي" },
  { url: "https://images.unsplash.com/photo-1674979724572-c0a0579bc9d0?w=1920&q=80", caption: "Jeddah", captionAr: "جدة" },
  { url: "https://images.unsplash.com/photo-1768058053585-023a8e052ada?w=1920&q=80", caption: "Dakar Rally, Saudi Arabia", captionAr: "رالي داكار، السعودية" },
  { url: "https://images.unsplash.com/photo-1780734323790-6f18edf42997?w=1920&q=80", caption: "Shebara Resort, Red Sea", captionAr: "منتجع شيبارة، البحر الأحمر" },
  { url: "https://images.unsplash.com/photo-1617778643338-44eafa2374c7?w=1920&q=80", caption: "SLS Red Sea Resort", captionAr: "منتجع إس إل إس، البحر الأحمر" },
  { url: "https://images.unsplash.com/photo-1729708790880-ccd1c603525c?w=1920&q=80", caption: "The Red Sea, Saudi Arabia", captionAr: "البحر الأحمر، السعودية" },
  { url: "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=1920&q=80", caption: "Red Sea Luxury Resort", captionAr: "منتجع فاخر، البحر الأحمر" },
];

export default function LoginPage() {
  const { login, API, loginWithGoogleCredential } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);

  // Remembered user
  const [savedUser, setSavedUser] = useState(null);

  const bg = useMemo(() => BG_IMAGES[Math.floor(Math.random() * BG_IMAGES.length)], []);
  const passkeyCheckId = useRef(0);

  useEffect(() => {
    if (window.__sessionExpired) {
      setSessionExpired(true);
      delete window.__sessionExpired;
    }
    const img = new Image();
    img.src = bg.url;
    img.onload = () => setImgLoaded(true);

    // Load saved user
    try {
      const stored = localStorage.getItem("lastUser");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.email && parsed?.name) {
          setSavedUser(parsed);
          setEmail(parsed.email);
        }
      }
    } catch {}
  }, [bg.url]); // eslint-disable-line

  // Detect passkey availability for whichever email is entered (remembered or typed)
  useEffect(() => {
    if (!email || !email.includes("@") || !email.includes(".")) {
      setHasPasskey(false);
      return;
    }
    const id = ++passkeyCheckId.current;
    const timer = setTimeout(() => {
      axios.post(`${API}/auth/passkey/login-options`, { email })
        .then(() => { if (passkeyCheckId.current === id) setHasPasskey(true); })
        .catch(() => { if (passkeyCheckId.current === id) setHasPasskey(false); });
    }, savedUser ? 0 : 500);
    return () => clearTimeout(timer);
  }, [email, API]); // eslint-disable-line

  const switchUser = () => {
    setSavedUser(null);
    setEmail("");
    setPassword("");
    setError("");
    setHasPasskey(false);
  };

  const handlePasskeyLogin = async (silent = false) => {
    setPasskeyLoading(true);
    setError("");
    try {
      // Get authentication options
      const optionsRes = await axios.post(`${API}/auth/passkey/login-options`, { email });
      // Start browser authentication (fingerprint/Face ID)
      const authentication = await startAuthentication({ optionsJSON: optionsRes.data });
      // Verify with backend
      const verifyRes = await axios.post(`${API}/auth/passkey/login-verify`, {
        email,
        credential: authentication,
      });
      const { token, user: usr } = verifyRes.data;
      localStorage.setItem("token", token);
      localStorage.setItem("lastUser", JSON.stringify({ name: usr.name, email: usr.email }));
      window.location.href = "/dashboard";
    } catch (err) {
      // Auto-triggered prompts fail silently — user just falls back to the password field
      if (!silent) {
        if (err.name === "NotAllowedError") {
          setError(t("passkey_cancelled") || "Authentication was cancelled");
        } else {
          setError(err?.response?.data?.detail || "Passkey login failed");
        }
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  // Auto-open the native passkey prompt for remembered users with a passkey, once detected
  const autoPasskeyTried = useRef(false);
  useEffect(() => {
    if (savedUser && hasPasskey && !autoPasskeyTried.current) {
      autoPasskeyTried.current = true;
      handlePasskeyLogin(true);
    }
  }, [savedUser, hasPasskey]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSessionExpired(false);
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      const status = err?.response?.status;
      if (status === 423) {
        setError(detail);
      } else if (detail.includes("attempts remaining")) {
        setError(detail);
      } else {
        setError(t("invalid_credentials"));
      }
    }
    setLoading(false);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const [googleLoading, setGoogleLoading] = useState(false);
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      try {
        await loginWithGoogleCredential(tokenResponse.access_token);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
      }
      setGoogleLoading(false);
    },
    onError: () => toast.error("Google sign-in failed"),
  });

  return (
    <div className="h-screen w-screen relative flex overflow-hidden bg-[var(--login-fade-color)]" dir="ltr">
      {/* Full-bleed photo — sits behind both panels; the form panel fades into it at the seam */}
      <div className="hidden lg:block absolute inset-0 bg-[#262626]">
        <div
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
          style={{ backgroundImage: `url(${bg.url})`, opacity: imgLoaded ? 1 : 0 }}
        />
        <div className="absolute inset-0 bg-[#1B1D21]/35" />
      </div>

      {/* Caption + logo over the photo */}
      <div className="hidden lg:block absolute bottom-8 start-10 z-10">
        <img src="/logo.png" alt="Inception" className="h-6 w-auto mb-4 rounded object-contain brightness-0 invert opacity-90" />
        <p className="text-white/90 text-sm font-medium tracking-wide">
          {lang === "ar" ? bg.captionAr : bg.caption}
        </p>
      </div>

      {/* Spacer so the fixed-width form panel pushes right on desktop */}
      <div className="hidden lg:block flex-1" />

      {/* Form panel — fades from transparent (revealing the photo) into the solid panel color */}
      <div
        className="relative flex-shrink-0 w-full lg:w-[520px] flex items-center justify-center px-6 sm:px-10"
        style={{ backgroundImage: "linear-gradient(to right, transparent 0px, var(--login-fade-color) 260px)" }}
        dir={lang === "ar" ? "rtl" : "ltr"}
      >
        {/* Language toggle */}
        <div className="absolute top-6 end-6 z-10">
          <button
            data-testid="login-lang-toggle"
            onClick={toggleLang}
            className="text-xs font-medium text-[#6F6C65] dark:text-[var(--dm-muted-text)] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text)] px-3 py-1.5 rounded-full border border-[#E4E1D9] dark:border-[var(--dm-input-border)] bg-white dark:bg-[var(--dm-input-bg)] transition-colors"
          >
            {lang === "en" ? "العربية" : "English"}
          </button>
        </div>

        <div className="relative z-0 w-full max-w-sm">
          <div className="bg-white dark:bg-[var(--dm-card)] rounded-[24px] p-8 shadow-[0_1px_2px_rgba(27,29,33,0.04),0_4px_12px_rgba(27,29,33,0.05),0_14px_32px_rgba(27,29,33,0.06)] dark:shadow-[var(--dm-card-shadow)]">
            <div className="mb-8">
              <img src="/logo.png" alt="Inception" className="w-[120px] h-auto mb-5 rounded object-contain" />
              {savedUser ? (
                <div>
                  <div className="w-12 h-12 rounded-full bg-[#F3CF5B] flex items-center justify-center mb-3">
                    <span className="text-lg font-bold text-[#1B1D21]">{savedUser.name.charAt(0).toUpperCase()}</span>
                  </div>
                  <h2 data-testid="welcome-back-name" className="text-lg font-semibold text-[#1B1D21] dark:text-[var(--dm-text)]">
                    {t("hi") || "Hi"}, {savedUser.name}
                  </h2>
                  <p className="text-xs text-[#6F6C65] dark:text-[var(--dm-muted-text)] mt-0.5">{savedUser.email}</p>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-semibold text-[#1B1D21] dark:text-[var(--dm-text)]">
                    RFQ Hub <span className="text-[#6F6C65] dark:text-[var(--dm-muted-text)] font-normal">منصة رفق</span>
                  </h2>
                </>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {sessionExpired && (
                <div data-testid="session-expired" className="p-3 bg-[#FBCC49]/15 border border-[#FBCC49]/30 rounded-lg text-sm text-[#9A6B00] dark:text-[#F0B44C]">
                  {t("session_expired")}
                </div>
              )}
              {error && (
                <div data-testid="login-error" className="p-3 bg-[#E44E50]/10 border border-[#E44E50]/25 rounded-lg text-sm text-[#B3261E] dark:text-[var(--dm-danger)]">
                  {error}
                </div>
              )}

              {/* Show email field only when no saved user */}
              {!savedUser && (
                <div>
                  <label className="block text-xs font-medium text-[#6F6C65] dark:text-[var(--dm-muted-text)] mb-1.5">{t("email")}</label>
                  <input
                    data-testid="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    required
                    autoFocus
                    className="w-full h-11 px-3 rounded-xl border border-[#E4E1D9] dark:border-[var(--dm-input-border)] bg-white dark:bg-[var(--dm-input-bg)] text-sm text-[#1B1D21] dark:text-[var(--dm-text)] placeholder:text-[#9B9792] dark:placeholder:text-[var(--dm-placeholder)] focus:outline-none focus:ring-2 focus:ring-[#FBCC49]/50 focus:border-[#FBCC49] transition-colors"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-[#6F6C65] dark:text-[var(--dm-muted-text)] mb-1.5">{t("password")}</label>
                <input
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus={!!savedUser}
                  className="w-full h-11 px-3 rounded-xl border border-[#E4E1D9] dark:border-[var(--dm-input-border)] bg-white dark:bg-[var(--dm-input-bg)] text-sm text-[#1B1D21] dark:text-[var(--dm-text)] placeholder:text-[#9B9792] dark:placeholder:text-[var(--dm-placeholder)] focus:outline-none focus:ring-2 focus:ring-[#FBCC49]/50 focus:border-[#FBCC49] transition-colors"
                />
              </div>

              <div className="flex justify-between items-center">
                {savedUser && (
                  <button
                    data-testid="switch-user-btn"
                    type="button"
                    onClick={switchUser}
                    className="text-xs text-[#6F6C65] dark:text-[var(--dm-muted-text)] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text)] font-medium transition-colors flex items-center gap-1"
                  >
                    <User className="w-3 h-3" />
                    {t("different_user") || "Different user"}
                  </button>
                )}
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-[#9A6B00] dark:text-[#F0B44C] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text)] font-medium ms-auto"
                >
                  {t("forgot_password")}
                </Link>
              </div>

              <button
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#FBCC49] text-[#1B1D21] rounded-xl text-sm font-semibold hover:bg-[#f5c431] disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("sign_in")}
              </button>

              {/* Passkey login — shown once the entered email is detected to have a registered passkey */}
              {hasPasskey && (
                <button
                  data-testid="passkey-login-btn"
                  type="button"
                  onClick={() => handlePasskeyLogin(false)}
                  disabled={passkeyLoading}
                  className="w-full h-11 bg-white dark:bg-[var(--dm-input-bg)] text-[#1B1D21] dark:text-[var(--dm-text)] border border-[#E4E1D9] dark:border-[var(--dm-input-border)] rounded-xl text-sm font-medium hover:border-[#1B1D21]/30 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
                >
                  {passkeyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
                  {t("sign_in_passkey") || "Sign in with Passkey"}
                </button>
              )}

              {/* Divider */}
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-[#E4E1D9] dark:bg-[var(--dm-divider)]" />
                <span className="text-[11px] text-[#9B9792] dark:text-[var(--dm-placeholder)] uppercase tracking-wider">{t("or") || "or"}</span>
                <div className="flex-1 h-px bg-[#E4E1D9] dark:bg-[var(--dm-divider)]" />
              </div>

              {/* Google Sign-In — REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <button
                data-testid="google-signin-btn"
                type="button"
                onClick={() => handleGoogleLogin()}
                disabled={googleLoading}
                className="w-full h-11 bg-white dark:bg-[var(--dm-input-bg)] text-[#1B1D21] dark:text-[var(--dm-text)] rounded-xl text-sm font-medium hover:border-[#1B1D21]/30 transition-colors flex items-center justify-center gap-2.5 border border-[#E4E1D9] dark:border-[var(--dm-input-border)] disabled:opacity-60"
              >
                {googleLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                )}
                {t("sign_in_google") || "Sign in with Google"}
              </button>
              <p className="text-center text-[10px] text-[#6F6C65] dark:text-[var(--dm-placeholder)] pt-1">v{process.env.REACT_APP_VERSION}</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
